import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Copy, Check, Gift, Users, Coins } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReferralWidget = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/referral/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Fehler beim Laden der Empfehlungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      toast.success('Link kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    }
  };

  if (loading) {
    return (
      <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle style={{ color: '#E6EDF3' }}>Empfehlungsprogramm</CardTitle>
        </CardHeader>
        <CardContent>
          <p style={{ color: '#94A3B8' }}>Lädt...</p>
        </CardContent>
      </Card>
    );
  }

  if (!referralData) return null;

  return (
    <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gift style={{ color: '#7CD4FD' }} size={24} />
          <CardTitle style={{ color: '#E6EDF3' }}>Empfehlungsprogramm</CardTitle>
        </div>
        <CardDescription style={{ color: '#94A3B8' }}>
          Empfehle Freunde und erhalte CHF 10 Guthaben pro erfolgreichem Abonnement
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Credits Display */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="flex items-center justify-center mb-2">
              <Coins style={{ color: '#22C55E' }} size={20} />
            </div>
            <div style={{ color: '#22C55E', fontSize: '1.5rem', fontWeight: 'bold' }}>
              CHF {referralData.available_credits.toFixed(2)}
            </div>
            <div style={{ color: '#94A3B8', fontSize: '0.875rem' }}>Verfügbar</div>
          </div>
          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="flex items-center justify-center mb-2">
              <Users style={{ color: '#7CD4FD' }} size={20} />
            </div>
            <div style={{ color: '#7CD4FD', fontSize: '1.5rem', fontWeight: 'bold' }}>
              {referralData.total_paid_referrals}
            </div>
            <div style={{ color: '#94A3B8', fontSize: '0.875rem' }}>Erfolgreich</div>
          </div>
          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
            <div className="flex items-center justify-center mb-2">
              <Coins style={{ color: '#A78BFA' }} size={20} />
            </div>
            <div style={{ color: '#A78BFA', fontSize: '1.5rem', fontWeight: 'bold' }}>
              CHF {referralData.pending_credits.toFixed(2)}
            </div>
            <div style={{ color: '#94A3B8', fontSize: '0.875rem' }}>Ausstehend</div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="space-y-2">
          <label style={{ color: '#E6EDF3', fontSize: '0.875rem', fontWeight: '500' }}>
            Dein Empfehlungslink
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralData.referral_url}
              readOnly
              className="flex-1 px-3 py-2 rounded-md"
              style={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                color: '#E6EDF3',
                fontSize: '0.875rem'
              }}
            />
            <Button
              onClick={copyToClipboard}
              style={{
                backgroundColor: copied ? '#22C55E' : '#7CD4FD',
                color: '#0B0F14',
                fontWeight: '600'
              }}
              className="flex items-center gap-2"
            >
              {copied ? (
                <>
                  <Check size={16} />
                  Kopiert
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Kopieren
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="pt-4 border-t" style={{ borderColor: '#1F2937' }}>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: '#94A3B8' }}>Klicks: </span>
              <span style={{ color: '#E6EDF3', fontWeight: '600' }}>
                {referralData.total_clicks}
              </span>
            </div>
            <div>
              <span style={{ color: '#94A3B8' }}>Registrierungen: </span>
              <span style={{ color: '#E6EDF3', fontWeight: '600' }}>
                {referralData.total_registrations}
              </span>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <div 
          className="p-4 rounded-lg text-sm"
          style={{ backgroundColor: '#1F2937', color: '#94A3B8' }}
        >
          💡 <strong style={{ color: '#E6EDF3' }}>So funktioniert's:</strong> Teile deinen Link mit Freunden. 
          Wenn sie sich anmelden und ein Abonnement abschließen, erhältst du CHF 10 Guthaben, 
          das du für deine Abonnements einlösen kannst.
        </div>
      </CardContent>
    </Card>
  );
};
